import { Button as HakiButton, type Radius } from "@hakistudio/hakiui";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "outline" | "ghost";
type Size = "sm" | "md" | "lg" | "icon";

/**
 * Text colours that meet the 4.5:1 floor on the Sebas palette.
 *
 * HakiUI sets `color: #ffffff` on primary and `color: var(--ui-primary)` on
 * secondary. Measured against our coral, both land at **2.93:1** — failing for
 * every primary action in the app. It spreads a passed `style` *after* its own,
 * so overriding here is enough; no `!important`, no CSS specificity fight.
 *
 * Primary uses `--sb-tile-ink` rather than `--sb-ink` because it must NOT flip
 * with the theme: coral stays coral in dark mode, where cream text on it would
 * measure 2.6:1. Fixed dark ink gives 5.9:1 in both themes — the same rule the
 * note tiles already follow: saturated accent, dark ink.
 */
const VARIANT_COLOR: Partial<Record<Variant, string>> = {
  primary: "var(--sb-tile-ink)",
  secondary: "var(--sb-ink)",
};

/**
 * Sebas button — HakiUI's Button with this app's shape and contrast defaults.
 * Kept as a wrapper so the pill radius and the `size="icon"` variant live in
 * one place and call sites stay unchanged.
 */
export function Button({
  variant = "primary",
  size = "md",
  radius = "full",
  className,
  style,
  ...props
}: Omit<React.ComponentProps<typeof HakiButton>, "size" | "variant" | "radius"> & {
  variant?: Variant;
  size?: Size;
  radius?: Radius;
}) {
  const isIcon = size === "icon";
  return (
    <HakiButton
      variant={variant}
      size={isIcon ? "md" : size}
      radius={radius}
      className={cn(isIcon && "aspect-square !px-0 justify-center", className)}
      style={{ ...(VARIANT_COLOR[variant] ? { color: VARIANT_COLOR[variant] } : {}), ...style }}
      {...props}
    />
  );
}
